import React, { useState, useEffect } from 'react';
import { signOut, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { useUser } from '../contexts/UserContext';

const Profile = () => {
  const { user } = useUser();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [authStatus, setAuthStatus] = useState('');
  const [profile, setProfile] = useState({
    fullName: '',
    idNumber: '',
    school: '',
    email: '',
    phone: '',
    grade: '12',
  });
  const [status, setStatus] = useState('');
  const [idError, setIdError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [appHistory, setAppHistory] = useState([]);
  const [appLoading, setAppLoading] = useState(true);
  const [appError, setAppError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    if (user === undefined) return; // auth loading
    if (user) {
      setProfile(prev => ({ ...prev, email: user.email || '' }));
      const fetchProfile = async () => {
        const docRef = doc(db, 'profiles', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProfile(prev => ({ ...prev, ...docSnap.data(), email: user.email || '' }));
        }
      };
      fetchProfile();
      // Fetch application history
      const q = query(collection(db, 'applications'), where('userId', '==', user.uid));
      const unsub = onSnapshot(q, (snapshot) => {
        setAppHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setAppLoading(false);
      }, (err) => {
        setAppError('Error loading applications.');
        setAppLoading(false);
      });
      return () => unsub();
    }
    // eslint-disable-next-line
  }, [user]);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await signOut(); // signOut gets auth instance from context
      window.location.reload();
    } catch (err) {
      alert('Error logging out.');
    }
    setLogoutLoading(false);
  };



  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // South African ID validation
  function isValidSAId(id) {
    if (!/^[0-9]{13}$/.test(id)) return false;
    // Luhn algorithm for SA ID
    let sum = 0;
    let alt = false;
    for (let i = id.length - 1; i >= 0; i--) {
      let n = parseInt(id.charAt(i), 10);
      if (alt) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alt = !alt;
    }
    return sum % 10 === 0;
  }
  // South African cellphone validation
  function isValidSAPhone(phone) {
    return /^0[6-8][0-9]{8}$/.test(phone);
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    setIdError('');
    setPhoneError('');
    let valid = true;
    if (!isValidSAId(profile.idNumber)) {
      setIdError('Enter a valid 13-digit South African ID number.');
      valid = false;
    }
    if (!isValidSAPhone(profile.phone)) {
      setPhoneError('Enter a valid South African cellphone number (e.g. 0821234567).');
      valid = false;
    }
    if (!valid) return;
    setLoading(true);
    setStatus('');
    try {
      await setDoc(doc(db, 'profiles', user.uid), profile);
      setStatus('Profile saved!');
      setProfileSaved(true);
    } catch (err) {
      setStatus('Error saving profile.');
    }
    setLoading(false);
  };


  if (user === undefined) {
    return <div>Loading...</div>;
  }
  if (!user) {
    return <div>You must be logged in to view this page.</div>;
  }
  return (
    <div style={{ maxWidth: 700, margin: '40px auto', padding: 24 }}>
      <h1 style={{ color: '#1a237e', fontSize: 32, fontWeight: 700, marginBottom: 24, marginTop: 0, textAlign: 'center', letterSpacing: 1 }}>My Profile</h1>
      {/* Editable Auth Profile Section */}
      <div style={{ background: '#fff', border: '1px solid #c7a16c', borderRadius: 16, boxShadow: '0 2px 12px #eee', padding: 32, marginBottom: 32 }}>
        <h2 style={{ color: '#1a237e', fontWeight: 700, fontSize: 22, marginBottom: 20, textAlign: 'center' }}>Account Settings</h2>
            <form onSubmit={async (e) => {
            e.preventDefault();
            setAuthStatus('');
            if (!user) return;
            // Re-authenticate if changing email or password
            let credential;
            if ((newEmail || newPassword) && currentPassword) {
              credential = EmailAuthProvider.credential(user.email, currentPassword);
              try {
                await reauthenticateWithCredential(user, credential);
              } catch (err) {
                setAuthStatus('Current password incorrect.');
                return;
              }
            }
            try {
              if (displayName && displayName !== user.displayName) {
                await updateProfile(user, { displayName });
              }
              if (newEmail) {
                await updateEmail(user, newEmail);
              }
              if (newPassword) {
                await updatePassword(user, newPassword);
              }
              setAuthStatus('Account updated successfully!');
              setNewEmail('');
              setNewPassword('');
              setCurrentPassword('');
            } catch (err) {
              setAuthStatus(err.message);
            }
          }}>
            <div className="mb-4">
              <label className="font-semibold block mb-1">Display Name</label>
              <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} className="w-full px-3 py-2 rounded border border-blue-400 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="mb-4">
              <label className="font-semibold block mb-1">New Email</label>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter new email" className="w-full px-3 py-2 rounded border border-blue-400 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="mb-4">
              <label className="font-semibold block mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" className="w-full px-3 py-2 rounded border border-blue-400 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            {(newEmail || newPassword) && (
              <div className="mb-4">
                <label className="font-semibold block mb-1">Current Password <span className="text-red-600">*</span></label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password to confirm" className="w-full px-3 py-2 rounded border border-blue-400 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
              </div>
            )}
            {authStatus && <div className={`text-center mb-3 font-semibold ${authStatus.includes('success') ? 'text-green-700' : 'text-red-700'}`}>{authStatus}</div>}
            <button type="submit" className="w-full py-3 rounded bg-gradient-to-r from-blue-900 to-blue-700 text-white font-bold text-lg mt-2 shadow hover:from-blue-700 hover:to-blue-900 transition">Update Account</button>
          </form>
          </div>
      {/* Avatar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '32px 0 24px 0' }}>
        <div style={{ width: 96, height: 96, borderRadius: '50%', background: 'linear-gradient(135deg, #1a237e, #3949ab)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 16px #bdbdbd', marginBottom: 12, border: '4px solid #fff', overflow: 'hidden' }}>
          <span style={{ fontSize: 48, fontWeight: 700, color: '#fff', userSelect: 'none' }}>
            {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : (
              <svg width="54" height="54" fill="#fff" viewBox="0 0 24 24"><path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/></svg>
            )}
          </span>
        </div>
      </div>
      {/* Profile Details Form */}
      {!profileSaved ? (
        <form onSubmit={handleSave} style={{ background: '#fff8e1', border: '1px solid #c7a16c', borderRadius: 16, padding: 32, boxShadow: '0 2px 12px #eee', maxWidth: 500, margin: '0 auto 32px auto' }}>
          <h2 className="text-center text-blue-800 mb-6 font-extrabold tracking-wide text-2xl drop-shadow">Grade 12 Learner Profile</h2>
          <div className="mb-4">
            <label className="font-semibold block mb-1">Full Name</label>
            <input name="fullName" type="text" value={profile.fullName} onChange={handleChange} required className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="mb-4">
            <label className="font-semibold block mb-1">ID Number</label>
            <input name="idNumber" type="text" value={profile.idNumber} onChange={handleChange} required className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            {idError && <div className="text-red-700 font-semibold text-sm mt-1">{idError}</div>}
          </div>
          <div className="mb-4">
            <label className="font-semibold block mb-1">School</label>
            <input name="school" type="text" value={profile.school} onChange={handleChange} required className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="mb-4">
            <label className="font-semibold block mb-1">Email</label>
            <input name="email" type="email" value={profile.email} readOnly className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 bg-gray-100 cursor-not-allowed" />
          </div>
          <div className="mb-4">
            <label className="font-semibold block mb-1">Phone</label>
            <input name="phone" type="text" value={profile.phone} onChange={handleChange} required className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            {phoneError && <div className="text-red-700 font-semibold text-sm mt-1">{phoneError}</div>}
          </div>
          <div className="mb-4">
            <label className="font-semibold block mb-1">Grade</label>
            <input name="grade" type="text" value={profile.grade} readOnly className="w-full px-3 py-2 rounded border border-yellow-600 mt-1 bg-gray-100 cursor-not-allowed" />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded bg-gradient-to-r from-blue-900 to-blue-700 text-white font-bold text-lg mt-2 shadow hover:from-blue-700 hover:to-blue-900 transition disabled:opacity-60"
          >
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
          {status && <p className={`text-center mt-4 font-semibold ${status.includes('Error') ? 'text-red-700' : 'text-green-700'}`}>{status}</p>}
        </form>
      ) : status && (
        <p className={`text-center mt-4 font-semibold ${status.includes('Error') ? 'text-red-700' : 'text-green-700'}`}>{status}</p>
      )}
      {/* Application History */}
      <div style={{ background: '#fff', border: '1px solid #c7a16c', borderRadius: 16, boxShadow: '0 2px 12px #eee', padding: 32, marginTop: 32 }}>
        <h3 style={{ color: '#a05a2c', fontWeight: 700, fontSize: 20, marginBottom: 24, textAlign: 'center' }}>Application History</h3>
        <div>
          {appLoading ? (
            <p>Loading application history...</p>
          ) : appError ? (
            <p style={{ color: '#c62828', textAlign: 'center' }}>{appError}</p>
          ) : appHistory.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center' }}>No applications found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: '100%', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12 }}>
                <thead style={{ background: '#a05a2c' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', color: '#fff', fontWeight: 700 }}>Institution/Fund</th>
                    <th style={{ padding: '12px 16px', color: '#fff', fontWeight: 700 }}>Type</th>
                    <th style={{ padding: '12px 16px', color: '#fff', fontWeight: 700 }}>Status</th>
                    <th style={{ padding: '12px 16px', color: '#fff', fontWeight: 700 }}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {appHistory.map(app => (
                    <tr key={app.id} style={{ borderTop: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '8px 16px' }}>{app.institution || '-'}</td>
                      <td style={{ padding: '8px 16px' }}>{app.type || '-'}</td>
                      <td style={{ padding: '8px 16px' }}>{app.status || '-'}</td>
                      <td style={{ padding: '8px 16px' }}>{app.deadline || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
